import {
  Button,
  Stack,
  Typography,
  alpha,
  styled,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useAuthContext } from 'src/auth/useAuthContext';
import { VerticalDivider } from 'src/components/VerticalDivider/VerticalDivider';
import Iconify from 'src/components/iconify';
import { useSubscription } from 'src/hooks/useSubscription';
import { useLocales } from 'src/locales';
import { useGlobalAlert } from './useGlobalAlert';
import { useGlobalAlertQuery } from './useGlobalAlertQuery';
import { useUpdateAlerts } from './useUpdateAlerts';
import { priorityColors } from './utils';

type Props = {
  colors: { background?: string; color?: string };
  children?: React.ReactNode;
};

const GlobalAlertContainerStyled = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  width: '100%',
  height: 1,
  gap: theme.spacing(3),
  padding: theme.spacing(2.3),
  gridArea: 'notification',
}));

export const GlobalAlertContainer = ({ colors, children }: Props) => {
  return (
    <GlobalAlertContainerStyled
      sx={{
        backgroundColor: colors.background,
        color: colors.color,
      }}
    >
      <Iconify icon="tabler:alert-triangle" />
      {children}
    </GlobalAlertContainerStyled>
  );
};

export const GlobalAlertDivider = ({ colors }: Props) => {
  if (!colors.color) return null;
  return (
    <VerticalDivider
      height="20px"
      sx={{
        backgroundColor: alpha(colors.color, 0.16),
      }}
    />
  );
};

export const GlobalAlert = () => {
  const theme = useTheme();

  const { alerts } = useGlobalAlertQuery();
  const { updateAlerts } = useUpdateAlerts();
  const { translate } = useLocales();

  const { freePlanMembersExceeded } = useSubscription();
  const { user } = useAuthContext();

  const { alert, setAlert, setHasAlert } = useGlobalAlert();

  useEffect(() => {
    setHasAlert(
      !!alerts?.length || !user?.verified.email || !!freePlanMembersExceeded,
    );
    if (!alerts?.length) return;
    const selectedAlert = alerts.find((alert) => !alert.dismissed);
    setAlert(selectedAlert || null);
  }, [alerts, setAlert, user, setHasAlert, freePlanMembersExceeded]);

  const handleDismissAlert = () => {
    if (!alert || !alert.dismiss) return;
    updateAlerts({
      ...alert,
      dismissed: true,
    });
  };

  const colors = useMemo(() => {
    if (!alert?.priority) return { backgound: undefined, color: undefined };
    const { background, color } = priorityColors(theme)[alert.priority];
    return { background, color };
  }, [alert?.priority, theme]);

  if (!alert) return null;

  return (
    <GlobalAlertContainer colors={colors}>
      <Typography variant="subtitle2">{alert.title}</Typography>

      <GlobalAlertDivider colors={colors} />

      <Typography variant="body2">{alert.body}</Typography>

      {!!alert?.action && (
        <>
          <GlobalAlertDivider colors={colors} />
          <Button
            size="small"
            variant="soft"
            color="inherit"
            sx={{ mx: 2, color: colors.color }}
            onClick={alert.action.fcn}
          >
            {alert.action.text}
          </Button>
        </>
      )}

      {alert.dismiss && (
        <>
          <GlobalAlertDivider colors={colors} />

          <Button
            size="small"
            variant="soft"
            color="inherit"
            sx={{ mx: 2, color: colors.color }}
            onClick={handleDismissAlert}
          >
            {translate('shared.buttons.dismiss')}
          </Button>
        </>
      )}
    </GlobalAlertContainer>
  );
};
